import { useEffect, useRef, type FocusEvent, type KeyboardEvent, type ReactNode } from 'react';

import { cx } from '@/components/ui/classNames';

import { readField, toCellText } from './cellValue';
import { GridCellEditor } from './GridCellEditor';
import type { ColumnValueType, GridColumn } from './types';

const FOCUSABLE = 'input, select, textarea, button, [tabindex]';
/** Kalender- und Listenpopups der Editoren liegen am `body`, nicht in der Zelle. */
const POPUP = '.e-popup, .e-dropdown-popup';

/**
 * Spaltentypen, die im Entwurf in der Monospace-Familie stehen (REQ-21 §4). Die Klasse sitzt
 * an der Zelle und nicht im Renderer: `DecimalCell` und `DateCell` bleiben unangetastet.
 */
const MONO_TYPES: readonly ColumnValueType[] = ['number', 'integer', 'date'];

/** Zahlen und Kennungen — Zell-Ids zählen unabhängig vom Datentyp dazu. */
function isMonoColumn<T>(column: GridColumn<T>): boolean {
  return column.isPrimaryKey || MONO_TYPES.includes(column.valueType);
}

export interface GridCellProps<T> {
  row: T;
  column: GridColumn<T>;
  selected: boolean;
  editing: boolean;
  onSelect: () => void;
  onStartEdit: () => void;
  onStopEdit: () => void;
  onCellSave: (item: T, columnName: string) => void;
}

export function GridCell<T>(props: GridCellProps<T>) {
  const { column, editing, onCellSave, onStopEdit, row } = props;
  const cellRef = useRef<HTMLTableCellElement>(null);
  const cancelled = useRef(false);

  // Der Editor bekommt den Fokus, damit Enter und Fokusverlust wie bei SfGrid greifen.
  useEffect(() => {
    if (!editing) return;
    cancelled.current = false;
    cellRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
  }, [editing]);

  /**
   * Bestätigt die Zelle: `onCellSave` melden und den Editor schließen. Es wird nichts lokal
   * gespeichert — 1:1 zu `args.Cancel = true; Grid.CloseEdit();`.
   */
  const commit = () => {
    if (cancelled.current) {
      cancelled.current = false;
      return;
    }
    onStopEdit();
    onCellSave(row, column.field);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTableCellElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit();
    } else if (event.key === 'Escape') {
      cancelled.current = true;
      onStopEdit();
    }
  };

  const onBlur = (event: FocusEvent<HTMLTableCellElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && event.currentTarget.contains(next)) return;
    // Ein offenes Popup gehört zum Editor (Kalender, Liste); die Zelle bleibt in Bearbeitung.
    if (document.querySelector(POPUP) !== null) return;
    commit();
  };

  const content = (): ReactNode => {
    if (editing) {
      if (column.editTemplate !== undefined) return column.editTemplate(row);
      return <GridCellEditor row={row} column={column} />;
    }
    if (column.cellTemplate !== undefined) return column.cellTemplate(row);
    return toCellText(readField(row, column.field));
  };

  return (
    <td
      ref={cellRef}
      className={cx(
        'e-rowcell',
        isMonoColumn(column) && 'iwm-num',
        props.selected && 'e-cellselectionbackground',
        editing && 'e-editedbatchcell',
      )}
      onClick={(event) => {
        // `detail === 2` gehört zum Doppelklick und darf die Auswahl nicht wieder aufheben.
        if (!editing && event.detail <= 1) props.onSelect();
      }}
      onDoubleClick={() => {
        if (column.allowEditing && !editing) props.onStartEdit();
      }}
      onMouseDown={
        editing
          ? (event) => {
              // Klicks auf Symbole des Editors dürfen den Fokus nicht aus der Zelle nehmen.
              if (!(event.target instanceof HTMLInputElement)) event.preventDefault();
            }
          : undefined
      }
      onKeyDown={editing ? onKeyDown : undefined}
      onBlur={editing ? onBlur : undefined}
    >
      {content()}
    </td>
  );
}

export default GridCell;
